import { intercomTarget } from '@/helpers/intercomTarget';
import { useGetAccountQuery } from '@/services/accounts';
import { useSendExternalApplicationUrlMutation } from '@/services/applications';
import { isErrorWithData } from '@/services/helpers';
import { useGetPerformProgressQuery } from '@/services/organizations';
import {
  Accordion,
  Badge,
  BadgeVariant,
  Box,
  Button,
  Card,
  Details,
  TextLink,
  toast,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  ApplicantType,
  ApplicationDocumentWithAdditionalApplicantData,
  FinancingDecision,
  RawApplicantWithApplicationData,
  UserPermissions,
} from 'types';

import { useGetCreditReportSummariesQuery } from '../../services/creditReports';
import { CreditReportSummary } from '../../types/CreditReports';
import { AccountRequirementStatus } from '../../types/Milestone';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import RequirePalmettoFinanceUser from '../auth/RequirePalmettoFinanceUser';
import RequirePermissions from '../auth/requirePermissions';
import CreditOverrideModal from '../CreditOverrideModal';
import FullCreditReportModal from '../FullCreditReportModal';
import { StatusIcon } from '../StatusIcon';

import { findCreditReportSummary } from './findCreditReportSummary';

interface CreditApplicationsProps {
  status: AccountRequirementStatus;
  applications: ApplicationDocumentWithAdditionalApplicantData[];
  firstApprovedApplication: ApplicationDocumentWithAdditionalApplicantData;
}
interface CreditApplicationDetailsProps {
  applicant: RawApplicantWithApplicationData;
  creditReports: CreditReportSummary[];
  handleViewCreditReport: (item: CreditReportSummary, applicantId?: string) => void;
  handleOpenCreditOverrideModal: (applicationId?: string) => void;
}

export const applicationStatus: {
  [key in FinancingDecision]: {
    label: string;
    variant: BadgeVariant;
  };
} = {
  [FinancingDecision.applicantNotFound]: {
    label: 'Applicant not found',
    variant: 'danger',
  },
  [FinancingDecision.approved]: {
    label: 'Approved',
    variant: 'success',
  },
  [FinancingDecision.approvedWithStipulations]: {
    label: 'Approved',
    variant: 'success',
  },
  [FinancingDecision.canceled]: {
    label: 'Canceled',
    variant: 'danger',
  },
  [FinancingDecision.creditFrozen]: {
    label: 'Credit Frozen',
    variant: 'danger',
  },
  [FinancingDecision.declined]: {
    label: 'Declined',
    variant: 'danger',
  },
  [FinancingDecision.error]: {
    label: 'Error',
    variant: 'danger',
  },
  [FinancingDecision.expired]: {
    label: 'Expired',
    variant: 'danger',
  },
  [FinancingDecision.new]: {
    label: 'New',
    variant: 'info',
  },
  [FinancingDecision.notChosen]: {
    label: 'Not Chosen',
    variant: 'info',
  },
  [FinancingDecision.pending]: {
    label: 'Pending',
    variant: 'warning',
  },
  [FinancingDecision.processing]: {
    label: 'Processing',
    variant: 'warning',
  },
  [FinancingDecision.withdrawn]: {
    label: 'Withdrawn',
    variant: 'warning',
  },
};
export const convertIsoToDateMed = (isoDate: string, opts = {}) =>
  isoDate ? DateTime.fromISO(isoDate, opts).toLocaleString(DateTime.DATE_MED) : null;

const badgeStyle = { width: 'fit-content' };
const textLinkStyle = {
  cursor: 'pointer',
  color: 'var(--color-text-body-primary)',
  textDecoration: 'underline',
  textAlign: 'right' as const,
};
const adminPermissions = [UserPermissions.admin];
const lightReachCreditAdminPermissions = [UserPermissions.lightReachCreditAdmin];
const adminEditorPermissions = [UserPermissions.admin, UserPermissions.editor];

function CreditApplicationRow({
  applicant,
  applicationId,
}: {
  applicant: RawApplicantWithApplicationData;
  applicationId: string;
}) {
  return (
    <Box direction="row" childGap="lg" alignItems="center">
      <Box width="xl">
        {applicant.status && (
          <Badge
            message={applicationStatus[applicant.status].label}
            variant={applicationStatus[applicant.status]?.variant}
            style={badgeStyle}
          />
        )}
      </Box>
      <Box flex="auto" childGap="lg">
        <Box childGap="2xs">
          <div>{`${applicant?.firstName} ${applicant?.lastName}`}</div>
          {applicant?.creditExpiryDate && (
            <Box color="body-secondary">{`credit expires ${convertIsoToDateMed(
              applicant.creditExpiryDate as unknown as string,
            )}`}</Box>
          )}
          {applicant?.creditDecisionDate && (
            <Box color="body-secondary">{`credit decision date ${convertIsoToDateMed(
              applicant.creditDecisionDate as unknown as string,
            )}`}</Box>
          )}
          {applicant.status &&
            (applicant.status === FinancingDecision.declined ||
              applicant.status === FinancingDecision.applicantNotFound) &&
            applicant.underwritingNotes?.length && (
              <RequirePalmettoFinanceUser>
                <Box color="body-secondary" fontSize="xs">{`${
                  applicant.status === FinancingDecision.declined ? 'Declined' : 'Applicant not found'
                } due to: ${applicant.underwritingNotes.join(', ')}`}</Box>
              </RequirePalmettoFinanceUser>
            )}
          {applicant?.isOverride && (
            <RequirePalmettoFinanceUser>
              <Box
                color="body-secondary"
                fontSize="xs"
              >{`Override approved by: ${applicant.overrideApprovedByEmail}`}</Box>
            </RequirePalmettoFinanceUser>
          )}
          <Box color="body-secondary" fontSize="xs">
            Application Id: {applicationId}
          </Box>
          {applicant?.ssnFormat && (
            <Box color="body-secondary" fontSize="xs">
              ssn format: {applicant.ssnFormat}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function CreditReportLink({
  creditReport,
  applicantId,
  onViewCreditReport,
}: {
  creditReport: CreditReportSummary;
  applicantId?: string;
  onViewCreditReport: (report: CreditReportSummary, applicantId?: string) => void;
}) {
  const handleClick = useCallback(() => {
    onViewCreditReport(creditReport, applicantId);
  }, [creditReport, applicantId, onViewCreditReport]);

  return (
    <TextLink style={textLinkStyle} onClick={handleClick}>
      Credit Report
    </TextLink>
  );
}

function CreditOverrideLink({
  applicationId,
  onCreditOverride,
}: {
  applicationId?: string;
  onCreditOverride: (applicationId?: string) => void;
}) {
  const handleClick = useCallback(() => {
    onCreditOverride(applicationId);
  }, [applicationId, onCreditOverride]);

  return (
    <TextLink style={textLinkStyle} onClick={handleClick}>
      Override Credit
    </TextLink>
  );
}

function CreditApplicationDetails({
  applicant,
  creditReports,
  handleViewCreditReport,
  handleOpenCreditOverrideModal,
}: CreditApplicationDetailsProps) {
  if (!applicant) {
    return;
  }
  const creditReport = findCreditReportSummary(creditReports, applicant);
  return (
    <Box
      padding="sm sm sm lg"
      width="100"
      childGap="lg"
      direction="row"
      alignItems="center"
      borderWidth="xs 0 0 0"
      borderColor="separator"
    >
      <Box direction="column" width="100" childGap="xs">
        {applicant &&
          applicant.status && ( // if the applicant has a status, show the row
            <Box childGap="xs">
              <Box direction="row" alignItems="center" justifyContent="space-between">
                <CreditApplicationRow applicant={applicant} applicationId={applicant.applicationId as string} />
                {creditReport && (
                  <RequirePalmettoFinanceUser>
                    <RequirePermissions permissions={adminPermissions}>
                      <CreditReportLink
                        creditReport={creditReport}
                        applicantId={creditReport.applicantId ?? (applicant.id ? String(applicant.id) : undefined)}
                        onViewCreditReport={handleViewCreditReport}
                      />
                    </RequirePermissions>
                  </RequirePalmettoFinanceUser>
                )}
                {applicant.status === FinancingDecision.declined && (
                  <RequirePermissions permissions={lightReachCreditAdminPermissions}>
                    <CreditOverrideLink
                      applicationId={(applicant.applicationId as unknown as string) || undefined}
                      onCreditOverride={handleOpenCreditOverrideModal}
                    />
                  </RequirePermissions>
                )}
              </Box>
            </Box>
          )}
      </Box>
    </Box>
  );
}

export function CreditApplications({ status, applications, firstApprovedApplication }: CreditApplicationsProps) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: any }>();
  const { data: account } = useGetAccountQuery(id);
  const isApproved = status === AccountRequirementStatus.completed;
  const { isOpen: isSectionOpen, handleToggle: handleToggleSection } = useOpenClose({ defaultIsOpen: !isApproved });
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const { isOpen: isShowAllApplicationsOpen, handleToggle: handleShowAllApplicationsToggle } = useOpenClose();
  const {
    isOpen: isCreditOverrideModalOpen,
    handleOpen: openCreditOverrideModal,
    handleClose: closeCreditOverrideModal,
  } = useOpenClose();
  const [selectedSummary, setSelectedSummary] = useState<CreditReportSummary | null>(null);
  const [applicantId, setApplicantId] = useState<string | undefined>();
  const [applicationId, setApplicationId] = useState<string | undefined>();
  const { data: creditReports = [] } = useGetCreditReportSummariesQuery(id);
  const [sendCreditApp] = useSendExternalApplicationUrlMutation();
  const [isSendingCreditApp, setIsSendingCreditApp] = useState(false);
  const allowsAction = useIsOrgReadOnly();

  const { data: canPerformProgressBasedOnOrgStatus } = useGetPerformProgressQuery(id);
  const primaryApplicant = account?.applicants?.find((applicant: any) => applicant.type === ApplicantType.primary);
  const [application] = applications;
  const firstApplicationForDisplay = firstApprovedApplication || application;
  const approvedApplicant = firstApplicationForDisplay?.applicants?.find(
    (applicant) =>
      applicant.status === FinancingDecision.approved ||
      applicant.status === FinancingDecision.approvedWithStipulations,
  );
  const shouldHideApplicationDetails = approvedApplicant && !firstApplicationForDisplay?.creditExpiryDate;

  //show the first approved applicant or the first primary applicant from the application
  const firstApplicantToDisplay =
    approvedApplicant ||
    firstApplicationForDisplay?.applicants?.find((applicant) => applicant.type === ApplicantType.primary);

  // find the additional applicants from the first application to display with the other applicants from the other applications
  const additionalApplicant = firstApplicationForDisplay?.applicants?.find(
    (applicant) => applicant.type !== firstApplicantToDisplay?.type && applicant.status,
  );
  const additionalApplicantsFromOtherApplications = applications.reduce(
    (acc: RawApplicantWithApplicationData[], curr: ApplicationDocumentWithAdditionalApplicantData) => {
      if (curr.id !== firstApplicationForDisplay?.id) {
        // credit checks will have a status on the applicant
        acc.push(
          ...curr.applicants
            .filter((applicant) => applicant.status)
            .map((applicant) =>
              Object.assign({}, applicant, {
                applicationId: curr.id,
                isOverride: curr.isOverride,
                overrideApprovedByEmail: curr.overrideApprovedByEmail,
              }),
            ),
        );
      }
      return acc;
    },
    [],
  );
  // add the additional applicant from the first application to the other applicants from the other applications, show it at the top
  if (additionalApplicant) {
    additionalApplicantsFromOtherApplications.unshift({
      ...additionalApplicant,
      applicationId: firstApplicationForDisplay?.id,
      isOverride: firstApplicationForDisplay?.isOverride,
      overrideApprovedByEmail: firstApplicationForDisplay?.overrideApprovedByEmail,
    } as RawApplicantWithApplicationData);
  }

  const handleCloseFullView = useCallback(() => {
    setApplicantId(undefined);
    setSelectedSummary(null);
    closeModal();
  }, [closeModal]);

  const selectedApplicantUnderwritingNotes = useMemo(() => {
    if (!applicantId) return undefined;
    for (const app of applications) {
      const found = app.applicants?.find((a) => String(a.id) === applicantId);
      if (found?.underwritingNotes) return found.underwritingNotes;
    }
    return undefined;
  }, [applicantId, applications]);

  const handleViewFull = useCallback(
    (item: CreditReportSummary, selectedApplicantId?: string) => {
      setApplicantId(selectedApplicantId);
      setSelectedSummary(item);
      openModal();
    },
    [openModal],
  );

  const handleCloseCreditOverrideModal = useCallback(() => {
    setApplicationId(undefined);
    closeCreditOverrideModal();
  }, [closeCreditOverrideModal]);

  const handleOpenCreditOverrideModal = useCallback(
    (selectedApplicationId?: string) => {
      setApplicationId(selectedApplicationId);
      openCreditOverrideModal();
    },
    [openCreditOverrideModal],
  );

  const handleNavigateToCreateApplication = useCallback(() => {
    navigate(`/accounts/${id}/application/create`);
  }, [navigate, id]);

  const handleSendCreditApp = useCallback(async () => {
    setIsSendingCreditApp(true);
    try {
      if (primaryApplicant?.email && primaryApplicant?.firstName) {
        await sendCreditApp({
          accountId: id,
          email: primaryApplicant?.email,
          firstName: primaryApplicant?.firstName,
        }).unwrap();
        toast.success('Application sent');
      } else {
        toast.error('Error sending application: No applicant email or name');
      }
    } catch (e) {
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        toast.error(`Error sending application: ${errorMessage}`);
      } else {
        console.error(e);
        toast.error('Error sending application');
      }
    }
    setIsSendingCreditApp(false);
  }, [primaryApplicant, id, sendCreditApp]);

  const firstApplicantWithApplicationData = useMemo(() => {
    if (!firstApplicantToDisplay) return null;
    return {
      ...firstApplicantToDisplay,
      applicationId: firstApplicationForDisplay?.id,
      isOverride: firstApplicationForDisplay?.isOverride,
      overrideApprovedByEmail: firstApplicationForDisplay?.overrideApprovedByEmail,
    };
  }, [firstApplicantToDisplay, firstApplicationForDisplay]);

  return (
    <>
      <FullCreditReportModal
        isModalOpen={isModalOpen}
        closeModal={handleCloseFullView}
        summary={selectedSummary}
        applicantId={applicantId}
        underwritingNotes={selectedApplicantUnderwritingNotes}
      />
      <CreditOverrideModal
        isModalOpen={isCreditOverrideModalOpen}
        closeModal={handleCloseCreditOverrideModal}
        applicationId={applicationId}
      />
      <Card.Section>
        <Accordion.Panel isOpen={isSectionOpen} borderWidth="0">
          <Accordion.PanelSummary
            isDetailsOpen={isSectionOpen}
            onToggle={isApproved ? handleToggleSection : undefined}
            hasCaret={isApproved ? undefined : false}
            padding="0"
          >
            <Box childGap="lg" alignItems="center" direction="row" justifyContent="space-between" flex="auto">
              <Box direction="row" alignItems="center">
                <Box padding="0 xs 0 0">
                  <StatusIcon status={status} size="md" />
                </Box>
                <Box
                  as="h3"
                  fontWeight="medium"
                  fontSize="md"
                  data-intercom-target={intercomTarget('account', 'credit-application')}
                >
                  Credit Application
                </Box>
              </Box>
              <Box direction="column" alignItems="flex-end" gap="sm">
                {isApproved && <Badge message="APPROVED" variant="success" />}
                <RequirePermissions permissions={adminEditorPermissions} checkAllPermissions={false}>
                  <Box direction="row" alignItems="flex-end" gap="sm">
                    <Button
                      disabled={!canPerformProgressBasedOnOrgStatus || !allowsAction}
                      isLoading={isSendingCreditApp}
                      onClick={handleSendCreditApp}
                      size="sm"
                      variant="primary"
                      tone="neutral"
                    >
                      Email Credit App
                    </Button>
                    <Button
                      as="button"
                      disabled={!canPerformProgressBasedOnOrgStatus || !allowsAction}
                      navigate={handleNavigateToCreateApplication}
                      size="sm"
                      variant="primary"
                      tone="neutral"
                    >
                      Run Credit Check
                    </Button>
                  </Box>
                </RequirePermissions>
              </Box>
            </Box>
          </Accordion.PanelSummary>
          {!shouldHideApplicationDetails && (
            <Accordion.PanelDetails padding="0">
              <Box childGap="lg" display="block" padding="md 0 0 0">
                {firstApplicantWithApplicationData && (
                  <CreditApplicationDetails
                    applicant={firstApplicantWithApplicationData}
                    handleViewCreditReport={handleViewFull}
                    handleOpenCreditOverrideModal={handleOpenCreditOverrideModal}
                    creditReports={creditReports}
                  />
                )}
                {additionalApplicantsFromOtherApplications?.length > 0 ? (
                  <Details isOpen={isShowAllApplicationsOpen}>
                    <Details.Summary
                      isDetailsOpen={isShowAllApplicationsOpen}
                      onToggle={handleShowAllApplicationsToggle}
                      display="inline"
                      padding="sm lg"
                    >
                      <Box>{isShowAllApplicationsOpen ? 'Hide All Applications' : 'Show All Applications'}</Box>
                    </Details.Summary>
                    {additionalApplicantsFromOtherApplications.map((applicant) => (
                      <CreditApplicationDetails
                        key={`${applicant.applicationId}-${applicant.type}`}
                        applicant={applicant}
                        handleViewCreditReport={handleViewFull}
                        handleOpenCreditOverrideModal={handleOpenCreditOverrideModal}
                        creditReports={creditReports}
                      />
                    ))}
                  </Details>
                ) : null}
              </Box>
            </Accordion.PanelDetails>
          )}
        </Accordion.Panel>
      </Card.Section>
    </>
  );
}
